import React, { useState, useEffect } from 'react';
import { Form, Button, Spinner } from 'react-bootstrap';
import { BiX, BiPlus, BiTrash, BiCamera } from 'react-icons/bi';
import Swal from 'sweetalert2';
import { TemplateField, FieldOption, CHOICE_TYPES, TextboxInputType, SUPPORTED_LANGUAGES } from '../../types/template';
import { uploadImage } from '../../utils/imageUpload';

interface Props {
    field: TemplateField;
    onChange: (updated: TemplateField) => void;
    /** Sets one option's image by id — safe to call after an async upload */
    onOptionImageChange: (fieldId: string, optionId: string, imageUrl: string | null) => void;
    onClose: () => void;
    languages: string[];
}

export default function FieldEditor({ field, onChange, onOptionImageChange, onClose, languages }: Props) {
    const [activeLang, setActiveLang] = useState(languages[0] ?? 'en');
    // keys: `${fieldId}:${optionId}` — the editor instance is reused across fields
    const [uploading, setUploading] = useState<Record<string, boolean>>({});

    // Reset active lang if it no longer exists in languages list
    useEffect(() => {
        if (!languages.includes(activeLang)) setActiveLang(languages[0] ?? 'en');
    }, [languages]); // eslint-disable-line

    const isChoice     = CHOICE_TYPES.includes(field.type);
    const isTextbox    = field.type === 'textbox';
    const isStarRating = field.type === 'star_rating';
    const isImageChoice = field.type === 'image_choice';

    const set = (patch: Partial<TemplateField>) => onChange({ ...field, ...patch });

    // ── Per-language label helpers ─────────────────────────────────────────
    const getLangLabel = () =>
        field.labels?.[activeLang] ?? (activeLang === 'en' ? field.label : '');

    const setLangLabel = (val: string) => {
        const labels = { ...(field.labels ?? {}), [activeLang]: val };
        // keep top-level label in sync with EN for backward compat
        set({ labels, ...(activeLang === 'en' ? { label: val } : {}) });
    };

    const getLangPlaceholder = () =>
        field.placeholders?.[activeLang] ?? (activeLang === 'en' ? field.placeholder ?? '' : '');

    const setLangPlaceholder = (val: string) => {
        const placeholders = { ...(field.placeholders ?? {}), [activeLang]: val };
        set({ placeholders, ...(activeLang === 'en' ? { placeholder: val } : {}) });
    };

    const getOptLabel = (opt: FieldOption) =>
        opt.labels?.[activeLang] ?? (activeLang === 'en' ? opt.label : '');

    const setOptLabel = (id: string, val: string) =>
        set({
            options: (field.options ?? []).map(o =>
                o.id === id
                    ? { ...o, labels: { ...(o.labels ?? {}), [activeLang]: val }, ...(activeLang === 'en' ? { label: val } : {}) }
                    : o
            ),
        });

    const addOption = () => {
        const opts = field.options ?? [];
        const newOpt: FieldOption = { id: `o_${Date.now()}`, label: `Option ${opts.length + 1}` };
        set({ options: [...opts, newOpt] });
    };

    const updateOptionImageUrl = (id: string, imageUrl: string) =>
        set({ options: (field.options ?? []).map(o => o.id === id ? { ...o, imageUrl: imageUrl || null } : o) });

    const handleOptionImageFile = async (optionId: string, input: HTMLInputElement) => {
        const file = input.files?.[0];
        input.value = ''; // allow picking the same file again
        if (!file) return;
        const fieldId = field.id;
        const key = `${fieldId}:${optionId}`;
        setUploading(u => ({ ...u, [key]: true }));
        try {
            const url = await uploadImage(file);
            onOptionImageChange(fieldId, optionId, url);
        } catch (err: any) {
            Swal.fire({ title: 'Upload failed', text: err?.message || 'Upload failed', icon: 'error', confirmButtonText: 'OK' });
        } finally {
            setUploading(u => { const { [key]: _, ...rest } = u; return rest; });
        }
    };

    const removeOption = (id: string) =>
        set({ options: (field.options ?? []).filter(o => o.id !== id) });

    const langName = (code: string) =>
        SUPPORTED_LANGUAGES.find(l => l.code === code)?.name ?? code.toUpperCase();

    return (
        <div className="field-editor">
            <div className="field-editor__title">
                Edit Field
                <button type="button" className="btn btn-sm btn-link p-0 text-muted" onClick={onClose} title="Close">
                    <BiX size={18} />
                </button>
            </div>

            {/* Language tabs */}
            {languages.length > 1 && (
                <div className="field-editor__lang-tabs">
                    {languages.map(l => (
                        <button
                            key={l}
                            type="button"
                            className={`field-editor__lang-tab${activeLang === l ? ' field-editor__lang-tab--active' : ''}`}
                            onClick={() => setActiveLang(l)}
                        >
                            {langName(l)}
                        </button>
                    ))}
                </div>
            )}

            {/* Label */}
            <Form.Group className="mb-3">
                <Form.Label>Question Label{languages.length > 1 && <span className="field-editor__lang-badge">{langName(activeLang)}</span>}</Form.Label>
                <Form.Control
                    size="sm"
                    value={getLangLabel()}
                    onChange={e => setLangLabel(e.target.value)}
                    placeholder="Enter your question"
                />
            </Form.Group>

            {/* Required */}
            <Form.Group className="mb-3">
                <Form.Check
                    type="switch"
                    id={`req-${field.id}`}
                    label="Required"
                    checked={field.required}
                    onChange={e => set({ required: e.target.checked })}
                />
            </Form.Group>

            {/* Placeholder — textbox + comment_box */}
            {(isTextbox || field.type === 'comment_box') && (
                <Form.Group className="mb-3">
                    <Form.Label>Placeholder{languages.length > 1 && <span className="field-editor__lang-badge">{langName(activeLang)}</span>}</Form.Label>
                    <Form.Control
                        size="sm"
                        value={getLangPlaceholder()}
                        onChange={e => setLangPlaceholder(e.target.value)}
                        placeholder="Hint text for respondent"
                    />
                </Form.Group>
            )}

            {/* Input type — textbox */}
            {isTextbox && (
                <Form.Group className="mb-3">
                    <Form.Label>Input Type</Form.Label>
                    <Form.Select
                        size="sm"
                        value={field.inputType ?? 'text'}
                        onChange={e => set({ inputType: e.target.value as TextboxInputType })}
                    >
                        <option value="text">Text</option>
                        <option value="email">Email</option>
                        <option value="phone">Phone</option>
                        <option value="number">Number</option>
                    </Form.Select>
                </Form.Group>
            )}

            {/* Min / Max value — number textbox */}
            {isTextbox && field.inputType === 'number' && (
                <div className="d-flex gap-2 mb-3">
                    <Form.Group style={{ flex: 1 }}>
                        <Form.Label>Min</Form.Label>
                        <Form.Control
                            size="sm" type="number"
                            placeholder="No limit"
                            value={field.minValue ?? ''}
                            onChange={e => set({ minValue: e.target.value === '' ? undefined : Number(e.target.value) })}
                        />
                    </Form.Group>
                    <Form.Group style={{ flex: 1 }}>
                        <Form.Label>Max</Form.Label>
                        <Form.Control
                            size="sm" type="number"
                            placeholder="No limit"
                            value={field.maxValue ?? ''}
                            onChange={e => set({ maxValue: e.target.value === '' ? undefined : Number(e.target.value) })}
                        />
                    </Form.Group>
                </div>
            )}

            {/* Max stars */}
            {isStarRating && (
                <Form.Group className="mb-3">
                    <Form.Label>Max Stars</Form.Label>
                    <Form.Select
                        size="sm"
                        value={field.maxStars ?? 5}
                        onChange={e => set({ maxStars: Number(e.target.value) })}
                    >
                        {[3, 4, 5, 6, 7, 10].map(n => (
                            <option key={n} value={n}>{n}</option>
                        ))}
                    </Form.Select>
                </Form.Group>
            )}

            {/* Options list — choice types */}
            {isChoice && (
                <Form.Group className="mb-3">
                    <Form.Label>Options{languages.length > 1 && <span className="field-editor__lang-badge">{langName(activeLang)}</span>}</Form.Label>
                    <div className="option-list">
                        {(field.options ?? []).map(opt => (
                            <div key={opt.id} className="option-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <Form.Control
                                        size="sm"
                                        value={getOptLabel(opt)}
                                        onChange={e => setOptLabel(opt.id, e.target.value)}
                                        placeholder="Option label"
                                    />
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => removeOption(opt.id)}
                                        title="Remove"
                                    >
                                        <BiTrash size={14} />
                                    </Button>
                                </div>
                                {isImageChoice && (() => {
                                    const isUploading = !!uploading[`${field.id}:${opt.id}`];
                                    return (
                                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                {opt.imageUrl
                                                    ? <img src={opt.imageUrl} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4, border: '1px solid #dee2e6', flexShrink: 0 }} />
                                                    : <div style={{ width: 48, height: 48, borderRadius: 4, background: '#f0f2f5', flexShrink: 0 }} />
                                                }
                                                <label
                                                    className={`btn btn-outline-primary btn-sm mb-0${isUploading ? ' disabled' : ''}`}
                                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}
                                                >
                                                    {isUploading
                                                        ? <><Spinner animation="border" size="sm" /> Uploading…</>
                                                        : <><BiCamera size={14} /> {opt.imageUrl ? 'Change image' : 'Upload / Take photo'}</>
                                                    }
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        hidden
                                                        disabled={isUploading}
                                                        onChange={e => handleOptionImageFile(opt.id, e.target)}
                                                    />
                                                </label>
                                                {opt.imageUrl && !isUploading && (
                                                    <Button variant="link" size="sm" className="text-danger p-0" onClick={() => updateOptionImageUrl(opt.id, '')}>
                                                        Remove
                                                    </Button>
                                                )}
                                        </div>
                                    );
                                })()}
                            </div>
                        ))}
                    </div>
                    <Button variant="outline-secondary" size="sm" onClick={addOption}>
                        <BiPlus size={14} /> Add Option
                    </Button>
                </Form.Group>
            )}
        </div>
    );
}
